import { Reference, useApolloClient } from '@apollo/client'
import {
  BalanceRefreshedDocument,
  TimesheetBalanceFragment,
  TimesheetBalanceFragmentDoc,
} from 'generated/resources/graphql'

import { useSubscription } from 'modules/core/hooks/useSubscription'

import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import AttendanceFeatureUtils from 'modules/attendance/lib/utils/AttendanceFeatures'

type AttendanceBalance = TimesheetBalanceFragment
type AttendanceBalancePool = TimesheetBalanceFragment['balancePools'][number]

interface Props {
  employeeId: string
}

const useSubscriptionBalanceRefreshed = ({ employeeId }: Props) => {
  const apolloClient = useApolloClient()
  const { featureByName } = useTimesheetData()

  useSubscription(BalanceRefreshedDocument, {
    onData: ({ data: refreshedData }) => {
      const refreshBalance = refreshedData.data?.attendanceBalanceRefresh?.balance

      if (!refreshBalance || refreshBalance?.employeeId != employeeId) return

      refreshBalance.balances.forEach((balance) => {
        apolloClient.cache.modify({
          id: `AttendanceEmployee:${employeeId}`,
          fields: {
            attendanceBalancesConnection(existingConnection, { readField }) {
              const id = `${employeeId}_${balance.date}`
              const balancePools: AttendanceBalancePool[] = (balance.balancePools || []).map(
                (pool) => ({
                  ...pool,
                  __typename: 'AttendanceBalancePool',
                  usages:
                    pool.usages?.map((usage) => ({
                      ...usage,
                      __typename: 'AttendanceTimeBlock',
                    })) || [],
                  transfers:
                    pool.transfers?.map((transfer) => ({
                      ...transfer,
                      __typename: 'AttendanceTimeBlock',
                    })) || [],
                })
              )

              const balanceWithId: AttendanceBalance = {
                ...balance,
                balancePools,
                id: id,
                __typename: 'AttendanceBalance',
              }

              const newBalanceRef = apolloClient.cache.writeFragment({
                data: balanceWithId,
                fragment: TimesheetBalanceFragmentDoc,
                fragmentName: 'TimesheetBalance',
              })

              if (
                (existingConnection.nodes || []).some(
                  (ref: Reference) => readField('id', ref) === id
                )
              ) {
                return existingConnection
              }

              return {
                ...existingConnection,
                nodes: [...existingConnection.nodes, newBalanceRef],
                totalCount: existingConnection.totalCount + 1,
              }
            },
          },
        })
      })
    },
    skip: !AttendanceFeatureUtils.hasBalanceMaterialized(featureByName),
  })
}

export default useSubscriptionBalanceRefreshed
